import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Feed from '../../components/Feed';
import UserPreferencesNoticeCard from '../../components/UserPreferencesNoticeCard';
import ActiveFeedFiltersBanner from '../../components/ActiveFeedFiltersBanner';
import { useNearbyPosts } from '../../../../shared/hooks/useNearbyPosts';
import { usePreferences } from '../../../../shared/hooks/usePreferences';
import { useAuth } from '../../../../core/providers/AuthProvider';
import { GEO } from '../../../../core/constants/geo';
import './styles.css';

const HomePage = ({ onActivityClick }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { preferences, get: getPreference, isLoading: preferencesLoading } = usePreferences();
  const isFirstRenderRef = useRef(true);
  const prevMaxDistanceRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const [isPageRefresh, setIsPageRefresh] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false);
  
  // Déterminer si c'est un premier chargement ou un rafraîchissement complet
  useEffect(() => {
    // Vérifier si c'est un rafraîchissement de page ou un chargement initial
    const isRefresh = !isFirstRenderRef.current && performance.navigation?.type === 1;
    setIsPageRefresh(isRefresh);
    
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
    }
    
    // Nettoyer la référence lors du démontage
    return () => {
      hasInitializedRef.current = false;
    };
  }, []);
  
  // Attendre que les préférences soient chargées
  useEffect(() => {
    if (!preferencesLoading) {
      // Un délai de sécurité pour s'assurer que tout est synchronisé
      const timer = setTimeout(() => {
        setPreferencesReady(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [preferencesLoading]);
  
  useEffect(() => {
    const loadUserData = async () => {
      if (refreshUser) {
        await refreshUser();
      }
    };
    
    // Charger les données utilisateur seulement au rafraîchissement complet
    if (isPageRefresh) {
      loadUserData();
    }
  }, [refreshUser, isPageRefresh]);
  
  // Récupérer les intérêts de l'utilisateur
  const userInterests = useMemo(() => {
    return user?.interests || [];
  }, [user?.interests]);
  
  // Stabiliser la référence à la valeur maxDistance
  const maxDistance = useMemo(() => {
    const rawValue = getPreference('maxDistance') || GEO.DEFAULT_RADIUS_KM;
    const value = Math.min(rawValue, GEO.MAX_RADIUS_KM);
    
    if (prevMaxDistanceRef.current === null) {
      prevMaxDistanceRef.current = value;
      console.log(`Initialisation de la distance: ${value}km`);
    } else {
      const hasChangedEnough = Math.abs(value - prevMaxDistanceRef.current) > 2;
      if (hasChangedEnough) {
        console.log(`Distance mise à jour: ${prevMaxDistanceRef.current}km -> ${value}km`);
        prevMaxDistanceRef.current = value;
      }
    }
    
    return value;
  }, [getPreference]);

  // Mémoriser la localisation pour éviter les re-renders inutiles
  // Priorité: currentPosition (GPS manuel) > user.location (profil) > preferences.location
  const userLocation = useMemo(() => {
    // 1. Utiliser currentPosition si définie (position GPS mise à jour manuellement)
    const currentPosition = getPreference('currentPosition');
    if (currentPosition?.latitude && currentPosition?.longitude) {
      return {
        latitude: currentPosition.latitude,
        longitude: currentPosition.longitude
      };
    }
    
    // 2. Sinon utiliser la localisation du profil
    if (user?.location?.coordinates) {
      return user.location.coordinates;
    }
    
    // 3. Sinon utiliser la localisation des préférences
    return preferences?.location?.coordinates;
  }, [getPreference, user?.location?.coordinates, preferences?.location?.coordinates]);
  
  // Stabiliser les options passées au hook
  const nearbyPostsOptions = useMemo(() => ({
    excludeUserPosts: false,
    limit: 20,
    maxDistanceOverride: maxDistance,
    userInterests: userInterests
  }), [maxDistance, userInterests]);
  
  // Ne pas rendre le hook tant que les préférences ne sont pas chargées
  const {
    posts,
    loading,
    hasMore,
    forceRefresh,
    deletePost,
    toggleParticipation,
    // eslint-disable-next-line no-unused-vars
    isEmpty: isEmptyPosts
  } = useNearbyPosts(
    preferencesReady && userLocation ? userLocation : null, 
    preferencesReady ? nearbyPostsOptions : { limit: 0 }
  );
  
  // Forcer un refresh après le montage si c'est un rafraîchissement de page
  const hasRefreshedRef = useRef(false);
  
  useEffect(() => {
    if (preferencesReady && isPageRefresh && userLocation && !hasRefreshedRef.current) {
      const timer = setTimeout(() => {
        console.log("Rafraîchissement de page détecté - forceRefresh");
        forceRefresh();
        hasRefreshedRef.current = true;
      }, 200);
      return () => clearTimeout(timer);
    }
    
    return () => {
      if (isPageRefresh || !userLocation) {
        hasRefreshedRef.current = false;
      }
    };
  }, [forceRefresh, userLocation, isPageRefresh, preferencesReady]);
  
  const loadMore = useCallback(() => {
    // Cette fonction est gérée directement dans useNearbyPosts
  }, []);

  useEffect(() => {
    if (posts.length > 0) {
      console.log(`${posts.length} posts trouvés.`);
    }
  }, [posts.length]);

  const handleShare = useCallback((postId) => {
    console.log('Share:', postId);
  }, []);

  const handleComment = useCallback((postId) => {
    console.log('Comment:', postId);
  }, []);

  const goToLocationSettings = useCallback(() => {
    navigate('/dashboard/settings');
  }, [navigate]);

  // Vérifier si les préférences sont par défaut
  const isUsingDefaultPreferences = useMemo(() => {
    // Toujours afficher la notice pour les besoins du test (retirez cette ligne en production)
    // Et loguer l'état des préférences pour le debug
    console.log('Debug preferences:', {
      maxDistance: getPreference('maxDistance'),
      preferencesReady,
      preferencesExist: !!preferences
    });
    
    // Simplement vérifier si maxDistance est undefined ou égal à 50
    return true; // Forcer l'affichage pour test
  }, [getPreference, preferencesReady, preferences]);

  // Définir l'état de vide basé sur la charge et le statut des préférences
  const isEmpty = posts.length === 0 && !loading;
  
  // Toujours désactiver le spinner
  const isLoading = false;

  return (
    <div className="home-page">
      {!userLocation && preferencesReady && (
        <div className="location-banner">
          <p>Pour voir les activités à proximité, veuillez configurer votre localisation</p>
          <button 
            onClick={goToLocationSettings}
            className="location-banner-button"
          >
            Configurer ma localisation
          </button>
        </div>
      )}
      
      {/* Bannière des filtres actifs */}
      {preferencesReady && userLocation && (
        <ActiveFeedFiltersBanner 
          maxDistance={maxDistance} 
          userInterests={userInterests}
        />
      )}
      
      {/* Carte d'information sur les préférences par défaut */}
      {preferencesReady && isUsingDefaultPreferences && (
        <UserPreferencesNoticeCard />
      )}
      
      <div className="home-content">
        <Feed 
          posts={posts}
          loading={isLoading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onShare={handleShare}
          onComment={handleComment}
          onDelete={deletePost}
          onToggleParticipation={toggleParticipation}
          isEmpty={isEmpty}
          emptyStateText={t('feed.emptyNearby')}
          onActivityClick={onActivityClick}
        />
      </div>
    </div>
  );
};

export default HomePage; 